import { useCallback, useEffect, useRef, useState } from 'react'
import { useMutation } from '@tanstack/react-query'

import {
  apiClient,
  AuthorRole,
  GenerateAssistantChatMessagePayload,
  GetFixedPromptTextGenerationOutState,
} from 'lib/apiClient'
import { handleError } from 'lib/sentry'

import { useAssistantChatMessagesContext } from './useAssistantChatMessagesContext'

const POLLING_INTERVAL_MS = 2000

export interface AssistantChatGenerationHook {
  generateAssistantResponse: (
    creationPayload?: GenerateAssistantChatMessagePayload,
  ) => void
  isLoading: boolean
  isError: boolean
  error?: Error
  isSubmitDisabled: boolean
}

export function useGenerateAssistantChatMessage(): AssistantChatGenerationHook {
  const { addChatMessageToThread, setPreviousSnapshotId } =
    useAssistantChatMessagesContext()

  const [isPolling, setIsPolling] = useState(false)
  const [isError, setIsError] = useState(false)
  const [error, setError] = useState<Error>()
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(false)

  const currentSequenceRef = useRef(0)
  const pollingTimeoutRef = useRef<number | null>(null)

  const handleRequestError = useCallback((err: any) => {
    handleError(err)
    setIsPolling(false)
    setIsError(true)
    setError(err instanceof Error ? err : new Error(String(err)))
    setIsSubmitDisabled(false)
  }, [])

  const cancelPolling = useCallback(() => {
    if (pollingTimeoutRef.current !== null) {
      clearTimeout(pollingTimeoutRef.current)
      pollingTimeoutRef.current = null
    }
  }, [])

  useEffect(() => () => cancelPolling(), [cancelPolling])

  const { mutateAsync: sendCustomerMessage, isPending: isGenerating } = useMutation({
    mutationFn: (payload: GenerateAssistantChatMessagePayload) =>
      apiClient.createNewCustomerMessage(payload),
    onError: handleRequestError,
  })

  const pollForAssistantResponse = useCallback(
    async (sequenceNumber: number) => {
      // Skip if not the most recent request
      if (sequenceNumber !== currentSequenceRef.current) return

      try {
        const response = await apiClient.getLatestAssistantChatMessage()

        // Check again after response received to handle race conditions
        if (sequenceNumber !== currentSequenceRef.current) return

        // Allow to add new messages as soon as we have a new dialogueSnapshotId
        if (response.dialogueSnapshotId) {
          setPreviousSnapshotId(response.dialogueSnapshotId)
          setIsSubmitDisabled(false)
        }

        switch (response.state) {
          case GetFixedPromptTextGenerationOutState.Generated:
            addChatMessageToThread({
              messageId: response.id,
              text: response.result.response.text,
              authorRole: AuthorRole.Agent,
              created: response.created,
            })
            setIsPolling(false)
            break

          case GetFixedPromptTextGenerationOutState.Pending:
          case GetFixedPromptTextGenerationOutState.InProgress:
            cancelPolling()

            pollingTimeoutRef.current = window.setTimeout(
              () => pollForAssistantResponse(sequenceNumber),
              POLLING_INTERVAL_MS,
            )
            break

          case GetFixedPromptTextGenerationOutState.Failed:
            addChatMessageToThread({
              messageId: response.id,
              text: `An error occurred while generating the response. Reason: ${
                response.result?.message ?? 'Unknown'
              }`,
              authorRole: AuthorRole.Agent,
              created: response.created,
            })
            setIsPolling(false)
            setIsSubmitDisabled(false)
            break
          // TODO: Decide how to handle deleted and default case
          case GetFixedPromptTextGenerationOutState.Deleted:
          default:
            handleRequestError(
              new Error(
                'Polling for assistant message has finished with an unhandled response state',
              ),
            )
            break
        }
      } catch (err) {
        if (sequenceNumber === currentSequenceRef.current) {
          handleRequestError(err)
        }
      }
    },
    [addChatMessageToThread, setPreviousSnapshotId, cancelPolling, handleRequestError],
  )

  const generateAssistantResponse = useCallback(
    async (creationPayload?: GenerateAssistantChatMessagePayload) => {
      cancelPolling()

      setIsError(false)
      setError(undefined)

      if (creationPayload) {
        try {
          currentSequenceRef.current += 1
          const sequence = currentSequenceRef.current

          setIsSubmitDisabled(true)

          await sendCustomerMessage(creationPayload)
          setIsPolling(true)
          pollForAssistantResponse(sequence)
        } catch (err) {
          handleRequestError(err)
        }
      } else {
        // For initial polling without a payload
        setIsPolling(true)
        pollForAssistantResponse(currentSequenceRef.current)
      }
    },
    [sendCustomerMessage, pollForAssistantResponse, cancelPolling, handleRequestError],
  )

  return {
    generateAssistantResponse,
    isLoading: isGenerating || isPolling,
    isError,
    error,
    isSubmitDisabled,
  }
}
